import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/api'
import {
  Search,
  CheckCircle2,
  XCircle,
  Shield,
  Crown,
  ExternalLink,
  Copy,
  Users as UsersIcon,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'

interface AgentRow {
  id: string
  name: string
  email: string
  role: string
  status: string
  isPlatformAdmin: boolean
  isSuperAdmin: boolean
  emailVerified: boolean
  emailVerifiedAt: string | null
  organization: {
    id: string
    name: string
    slug: string
    status: string
  } | null
  createdAt: string
  lastLoginAt: string | null
  lastActive: string | null
}

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    const today = new Date()
    const diffMs = today.getTime() - d.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return '—'
  }
}

const formatLastSeen = (iso: string | null | undefined) => {
  if (!iso) return 'Never'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return 'Never'
    const diffMs = Date.now() - d.getTime()
    const mins = Math.floor(diffMs / 60_000)
    if (mins < 10) return 'Online now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days < 7) return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return 'Never'
  }
}

const isActuallyOnline = (iso: string | null | undefined) => {
  if (!iso) return false
  const d = new Date(iso).getTime()
  return !Number.isNaN(d) && Date.now() - d < 10 * 60_000
}

type AccountState = 'active' | 'pending' | 'no-org' | 'suspended' | 'cancelled'

const deriveAccountState = (agent: AgentRow): AccountState => {
  if (!agent.organization) return 'no-org'
  const orgStatus = agent.organization.status
  if (orgStatus === 'suspended') return 'suspended'
  if (orgStatus === 'cancelled') return 'cancelled'
  if (!agent.emailVerified) return 'pending'
  return 'active'
}

const stateTone: Record<AccountState, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-800/40',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200/60 dark:border-amber-800/40',
  'no-org': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200/60 dark:border-amber-800/40',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200/60 dark:border-red-800/40',
  cancelled: 'bg-muted text-muted-foreground border-border',
}

const stateLabel: Record<AccountState, string> = {
  active: 'Active',
  pending: 'Email unverified',
  'no-org': 'No org',
  suspended: 'Org suspended',
  cancelled: 'Org cancelled',
}

export default function UsersAdminPage() {
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const { toast } = useToast()

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250)
    return () => clearTimeout(t)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { limit: 500, offset: 0 }
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim()
      const res = await api.get('/api/platform/agents', { params })
      setAgents(res.data.agents || [])
      setTotal(res.data.total ?? (res.data.agents?.length || 0))
    } catch (err: any) {
      toast({
        title: 'Failed to load users',
        description: err?.response?.data?.message || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, toast])

  useEffect(() => {
    load()
  }, [load])

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email)
      toast({ title: 'Copied', description: email })
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' })
    }
  }

  const verifiedCount = agents.filter(a => a.emailVerified).length
  const adminCount = agents.filter(a => a.isPlatformAdmin || a.isSuperAdmin).length
  const noOrgCount = agents.filter(a => !a.organization).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">All Users</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every agent account across every organization. Useful for diagnosing login and reset issues.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <UsersIcon className="h-4 w-4" strokeWidth={1.75} />
          {loading ? 'Loading…' : <><span className="font-medium text-foreground">{total}</span> user{total === 1 ? '' : 's'}</>}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
            <div className="text-2xl font-semibold mt-1">{total}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Verified</div>
            <div className="text-2xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400">{verifiedCount}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Admins</div>
            <div className="text-2xl font-semibold mt-1 text-brand-royal">{adminCount}</div>
          </CardContent>
        </Card>
        <Card className="card-inset">
          <CardContent className="py-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">No Org</div>
            <div className="text-2xl font-semibold mt-1 text-amber-600 dark:text-amber-400">{noOrgCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search + table */}
      <Card className="card-inset">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base">Users</CardTitle>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
              <Input
                placeholder="Search by email, name, or organization…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && agents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                      Loading users…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && agents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                      {debouncedSearch ? `No users match "${debouncedSearch}"` : 'No users yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {agents.map(agent => {
                  const state = deriveAccountState(agent)
                  const online = isActuallyOnline(agent.lastActive)
                  return (
                    <TableRow key={agent.id} className="group">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[220px]" title={agent.email}>{agent.email}</span>
                          <button
                            type="button"
                            onClick={() => copyEmail(agent.email)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 -m-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                            title="Copy email"
                          >
                            <Copy className="h-3 w-3" strokeWidth={2} />
                          </button>
                          {agent.isSuperAdmin && (
                            <span title="Super Admin">
                              <Crown className="h-3.5 w-3.5 text-violet-500 shrink-0" strokeWidth={2} />
                            </span>
                          )}
                          {!agent.isSuperAdmin && agent.isPlatformAdmin && (
                            <span title="Platform Admin">
                              <Shield className="h-3.5 w-3.5 text-brand-royal shrink-0" strokeWidth={2} />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{agent.name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-sm">
                        {agent.organization ? (
                          <Link
                            to={`/admin/organizations/${agent.organization.id}`}
                            className="inline-flex items-center gap-1 hover:text-brand-royal transition-colors"
                          >
                            <span className="truncate max-w-[180px]" title={agent.organization.name}>{agent.organization.name}</span>
                            <ExternalLink className="h-3 w-3 opacity-60" strokeWidth={2} />
                          </Link>
                        ) : (
                          <span className="text-muted-foreground text-xs italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm capitalize text-muted-foreground">{agent.role || '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={stateTone[state]}>
                            {stateLabel[state]}
                          </Badge>
                          {state === 'active' && (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" strokeWidth={2} />
                          )}
                          {state === 'pending' && (
                            <XCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" strokeWidth={2} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          {online && (
                            <span className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.15)] shrink-0" title="Active within 10 min" />
                          )}
                          <span className={online ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-muted-foreground'}>
                            {formatLastSeen(agent.lastActive)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(agent.createdAt)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
